import type {
  NarrativeOcgFinding,
  NarrativeOcgSeverity,
  NarrativeOcgValidationHint,
} from '../types'

const isValidationFinding = (
  finding: NarrativeOcgFinding
): finding is NarrativeOcgFinding & { severity: 'error' | 'warning' } =>
  finding.severity === 'error' || finding.severity === 'warning'

/**
 * Returns true when the validation hint carries server-supplied
 * instructions. In that case the panel must render the instructions
 * text instead of the narrative diff and only expose the Discard action.
 */
export const hasInstructions = (
  validationHint?: NarrativeOcgValidationHint
): boolean =>
  Boolean(
    validationHint?.visible &&
      Array.isArray(validationHint.instructions) &&
      validationHint.instructions.length > 0
  )

export const hasOcgValidationFindings = (
  validationHint?: NarrativeOcgValidationHint
) =>
  Boolean(
    validationHint?.visible &&
      validationHint.findings?.some(isValidationFinding)
  )

export const getSeverity = (validationHint?: NarrativeOcgValidationHint) =>
  validationHint?.visible
    ? validationHint.findings?.some(finding => finding.severity === 'error')
      ? 'error'
      : validationHint.findings?.some(
            finding => finding.severity === 'warning'
          )
        ? 'warning'
        : undefined
    : undefined

export const getNarrativeSeverity = (
  validationHint?: NarrativeOcgValidationHint
): NarrativeOcgSeverity | undefined => {
  if (!validationHint?.visible) return undefined

  const findings = validationHint.findings ?? []
  const hasCategorizedFindings = findings.some(finding =>
    Boolean(finding.violation_category)
  )
  if (!hasCategorizedFindings) return getSeverity(validationHint)

  const narrativeFindings = findings.filter(finding => {
    const category = finding.violation_category?.toLowerCase()
    return category === 'narrative'
  })
  if (narrativeFindings.some(finding => finding.severity === 'error'))
    return 'error'
  if (narrativeFindings.some(finding => finding.severity === 'warning'))
    return 'warning'
  return narrativeFindings[0]?.severity
}

export const hasErrors = (validationHint?: NarrativeOcgValidationHint) =>
  Boolean(
    validationHint?.visible &&
      validationHint.findings?.some(finding => finding.severity === 'error')
  )

export const hasWarnings = (validationHint?: NarrativeOcgValidationHint) =>
  Boolean(
    validationHint?.visible &&
      validationHint.findings?.some(finding => finding.severity === 'warning')
  )

export const hasOnlyNonBlockingFieldWarnings = (
  validationHint?: NarrativeOcgValidationHint
) => {
  if (!validationHint?.visible || hasErrors(validationHint)) return false

  const warnings = (validationHint.findings ?? []).filter(
    finding => finding.severity === 'warning'
  )
  if (!warnings.length) return false

  if (validationHint.validationScope === 'fields') {
    return warnings.every(
      finding => finding.violation_category?.toLowerCase() !== 'narrative'
    )
  }

  return warnings.every(finding => {
    const category = finding.violation_category?.toLowerCase()
    return category === 'time' || category === 'activity' || category === 'coding'
  })
}

export const getIssueCount = (
  validationHint: NarrativeOcgValidationHint | undefined,
  severity: NarrativeOcgSeverity
) => {
  if (!validationHint?.visible) return 0
  return (validationHint.findings ?? []).filter(
    finding => finding.severity === severity
  ).length
}

export const getIssueTitle = (
  severity: NarrativeOcgSeverity,
  count: number
) => {
  const label =
    severity === 'warning' ? 'Warning' : severity === 'error' ? 'Error' : 'Info'
  return `${label} (${count})`
}

export const getIssueDetails = (
  validationHint: NarrativeOcgValidationHint | undefined,
  severity?: NarrativeOcgSeverity
) => {
  const findingDetails = validationHint?.findings
    ?.filter(
      finding =>
        !severity || (finding.severity ?? validationHint.severity) === severity
    )
    .map(finding => ({
      title: finding.rule_name || finding.ruleName || '',
      description: finding.message,
    }))

  if (findingDetails?.length) return findingDetails

  const details = validationHint?.ruleHits
    ?.filter(
      rule =>
        !severity || (rule.severity ?? validationHint.severity) === severity
    )
    .map(rule => ({
      title: rule.rule_name || rule.ruleName,
      description: rule.message,
    }))

  return details || []
}
